import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ConfirmDialogHost } from "@/components/ui/confirm-dialog";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "@/lib/queryClient";
import { installLegacyEventBridge } from "@/lib/invalidate";
import { Menu, X, Home, Loader2 } from "lucide-react";

import { useReminders } from "./offline/useReminders";

/** Mounts reminder timers while the user is logged in; renders nothing. */
function ReminderScheduler() {
  useReminders();
  return null;
}

// Eager pages (always reached on first paint)
import Landing from "./pages/Landing";
import LandingNew from "./pages/LandingNew";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LoginOtp from "./pages/LoginOtp";

// Lazy pages — split out of the main bundle
const Invitation = lazy(() => import("./pages/Invitation"));
const SetupOrganization = lazy(() => import("./pages/SetupOrganization"));
const Requests = lazy(() => import("./pages/Requests"));
const Approvals = lazy(() => import("./pages/Approvals"));
const LeaveCalendar = lazy(() => import("./pages/LeaveCalendar"));
const FinanceDashboard = lazy(() => import("./pages/FinanceDashboard"));
const Requisitions = lazy(() => import("./pages/Requisitions"));
const People = lazy(() => import("./pages/People"));
const Departments = lazy(() => import("./pages/Departments"));
const Budgets = lazy(() => import("./pages/Budgets"));
const ProjectBudgetPlan = lazy(() => import("./pages/ProjectBudgetPlan"));
const ProjectBudgetVarianceReport = lazy(() => import("./pages/ProjectBudgetVarianceReport"));
const MyRequests = lazy(() => import("./pages/MyRequests"));
const AuditLog = lazy(() => import("./pages/AuditLog"));

// Onboarding — the single post-provisioning wizard at /welcome (plan 024).
const Welcome = lazy(() => import("./pages/onboarding/Welcome"));

// Shells
import ProtectedRoute from "./app/ProtectedRoute";
import GuestRoute from "./app/GuestRoute";
import RequireOrg from "./app/RequireOrg";
import ErrorBoundary from "./components/ErrorBoundary";
import { authStore } from "@/store/auth";

// New Intercom AppShell (dark) + its pages
const AppShell = lazy(() => import("./app/AppShell"));
const ShellHome = lazy(() => import("./pages/app/Home"));
const MyWork = lazy(() => import("./pages/app/MyWork"));
const AppProjects = lazy(() => import("./pages/app/Projects"));
const AppPortfolio = lazy(() => import("./pages/app/Portfolio"));
const AppOverdue = lazy(() => import("./pages/app/OverdueDashboard"));
const AppProjectDetail = lazy(() => import("./pages/app/ProjectDetail"));
const AppTaskDetail = lazy(() => import("./pages/app/TaskDetail"));
const AppTemplates = lazy(() => import("./pages/app/Templates"));
const AppApprovals = lazy(() => import("./pages/app/ApprovalsInbox"));
const AppSettings = lazy(() => import("./pages/app/Settings"));
const Placeholder = lazy(() => import("./pages/app/Placeholder"));

function PageLoading() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-black/40 text-sm">
      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
      Loading…
    </div>
  );
}

function AppContent() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, initializing, init } = authStore();

  useEffect(() => {
    if (initializing) {
      init();
    }
  }, [initializing, init]);

  // Bridge the legacy `window` event bus into React Query invalidation so
  // legacy dispatchers refresh RQ caches during the tiered migration.
  useEffect(() => installLegacyEventBridge(), []);

  // Capture access_token from URL hash after OAuth and clean it up
  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("access_token=")) {
      const params = new URLSearchParams(hash.replace("#", ""));
      const token = params.get("access_token");
      if (token) {
        authStore.getState().setAccessToken(token);
        authStore
          .getState()
          .fetchMe()
          .catch(() => {});
        // remove token from URL
        const { pathname, search } = window.location;
        window.history.replaceState(null, "", pathname + search);
      }
    }
  }, []);

  // Navigation items — the legacy header below only ever needs a way back
  // into the app now that the old shell's own nav items are gone.
  const navItems = [{ path: "/app", label: "Dashboard", icon: Home }];

  // The new Intercom-style /app shell renders its own chrome; the legacy
  // top-bar header below renders only for pages outside it (onboarding, invitation).
  const isAppShell =
    location.pathname === "/app" || location.pathname.startsWith("/app/");

  // Public/auth routes must never render the authenticated chrome (navbar etc.),
  // even for a brief moment while a session is being restored. GuestRoute already
  // redirects authenticated users away from these; this is belt-and-suspenders.
  const publicRoutes = new Set([
    "/",
    "/login",
    "/login/otp",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/landing/classic",
    "/landing/new",
    // Full-bleed two-panel screens like Register/Login, not app-shell content —
    // the legacy top-bar must not paint over either (plan 024 step 10).
    "/setup-organization",
    "/welcome",
  ]);
  const isPublicRoute = publicRoutes.has(location.pathname);
  const showAppChrome = !!user && !isAppShell && !isPublicRoute;

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header - Only show when authenticated and not on dashboard, schedule, or team */}
      {showAppChrome && (
        <header className="border-b border-border bg-card">
          <nav className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link to="/dashboard" className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-gold-gradient" />
                <span className="text-xl font-semibold">Granula</span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-6">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2"
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Mobile Navigation */}
            {isMobileMenuOpen && (
              <div className="md:hidden py-4 border-t border-border">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-2 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </nav>
        </header>
      )}

      {/* Reminder alarm scheduler — renders nothing, fires notifications */}
      {user && !isPublicRoute && <ReminderScheduler />}

      {/* Main Content */}
      <main
        className={showAppChrome ? "container mx-auto px-4 py-8" : ""}
      >
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* Public Routes - landing page hidden for now; "/" sends visitors straight to login */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/landing/new" element={<LandingNew />} />
            <Route path="/landing/classic" element={<Landing />} />
            <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
            <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
            <Route path="/reset-password" element={<GuestRoute><ResetPassword /></GuestRoute>} />
            <Route path="/login/otp" element={<GuestRoute><LoginOtp /></GuestRoute>} />

            {/* Tenant provisioning (plan 024 / ADR-0009) — authenticated but
                deliberately OUTSIDE RequireOrg, since it's the destination
                RequireOrg redirects to. Wrapping it would redirect to itself. */}
            <Route
              path="/setup-organization"
              element={
                <ProtectedRoute>
                  <SetupOrganization />
                </ProtectedRoute>
              }
            />
            {/* The post-provisioning setup wizard (plan 024 step 12): three
                skippable steps — first department, invite people, first project
                — that end by marking onboarding_complete and handing over to
                /app. Inside RequireOrg because every step writes org-scoped data. */}
            <Route
              path="/welcome"
              element={
                <ProtectedRoute>
                  <RequireOrg>
                    <Welcome />
                  </RequireOrg>
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            {/* The new Intercom-style shell at /app/* is the canonical post-login
                surface. Anything still pointing at /dashboard (legacy login flows,
                OAuth callback, bookmarks) lands there too. The old Dashboard
                component is gone from the route table. */}
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            <Route
              path="/projects"
              element={<Navigate to="/app/projects" replace />}
            />

            {/* ── Intercom-style AppShell (dark) — the canonical post-login surface ── */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <RequireOrg>
                    <AppShell />
                  </RequireOrg>
                </ProtectedRoute>
              }
            >
              <Route index element={<ShellHome />} />
              <Route path="approvals" element={<AppApprovals />} />
              <Route path="overdue" element={<AppOverdue />} />
              <Route path="projects" element={<AppProjects />} />
              <Route path="projects/portfolio" element={<AppPortfolio />} />
              <Route path="projects/:id" element={<AppProjectDetail />} />
              <Route path="projects/templates" element={<AppTemplates />} />
              <Route path="work" element={<MyWork />} />
              <Route path="work/assigned" element={<MyWork />} />
              <Route path="work/created" element={<MyWork />} />
              <Route path="work/reminders" element={<MyWork />} />
              <Route path="work/tasks/:id" element={<AppTaskDetail />} />
              <Route path="requests" element={<MyRequests />} />
              {/* Module 1 — Administration (HR + Finance) */}
              <Route path="people" element={<People />} />
              <Route path="people/departments" element={<Departments />} />
              {/* `key` is load-bearing: Requests/Approvals each render at two
                  sibling routes under /app, and `defaultTab` is only read as
                  useState's initial value. Without a differing key React
                  reconciles the same instance across the navigation (same
                  component type, same tree position) and the tab never
                  changes. The key forces a remount so defaultTab re-applies. */}
              <Route path="people/leave" element={<Requests key="leave" defaultTab="leave" mode="leave" />} />
              <Route path="people/leave-calendar" element={<LeaveCalendar />} />
              <Route path="people/leave-approvals" element={<Approvals key="leave" defaultTab="leave" mode="leave" />} />
              <Route path="people/audit" element={<AuditLog />} />
              <Route path="finance" element={<FinanceDashboard />} />
              <Route path="finance/expenses" element={<Requests key="expenses" defaultTab="expenses" mode="expenses" />} />
              <Route path="finance/budgets" element={<Budgets />} />
              <Route path="finance/budgets/:budgetId/plan" element={<ProjectBudgetPlan />} />
              <Route path="finance/reports" element={<ProjectBudgetVarianceReport />} />
              <Route path="finance/requisitions" element={<Requisitions />} />
              <Route path="finance/approvals" element={<Approvals key="expenses" defaultTab="expenses" mode="finance" />} />
              <Route
                path="reports"
                element={
                  <Placeholder
                    title="My reports"
                    blurb="Daily / weekly / monthly reports compiled from your activity."
                    milestone="Milestone 3"
                  />
                }
              />
              <Route
                path="reports/team"
                element={
                  <Placeholder
                    title="Team reports"
                    blurb="Team-level activity, leave and expense rollups."
                    milestone="Milestone 3"
                  />
                }
              />
              <Route
                path="reports/org"
                element={
                  <Placeholder
                    title="Organization reports"
                    blurb="Org-wide financial and HR overview (owner only)."
                    milestone="Milestone 3"
                  />
                }
              />
              <Route
                path="assistant"
                element={
                  <Placeholder
                    title="Assistant"
                    blurb="Natural-language queries, report summaries, and meeting-notes → tasks."
                    milestone="Milestone 3 · Premium AI"
                  />
                }
              />
              <Route path="settings" element={<AppSettings />} />
            </Route>

            {/* Invitation landing — auth handled inside */}
            <Route path="/invitation/:token" element={<Invitation />} />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      {/* Toast notifications */}
      <Toaster position="bottom-right" richColors />
      {/* The one host for confirmDialog() — see components/ui/confirm-dialog.tsx */}
      <ConfirmDialogHost />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AppContent />
        </Router>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
